import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { MapPin, BadgeCheck, ExternalLink, ChevronRight, Loader2 } from "lucide-react";

const CITY_KEY = "hp_detected_city";

/**
 * Homepage band that auto-detects the visitor's city via ipapi.co (free, no key)
 * and shows a small list of registered + internet-listed homeopaths in that city.
 *
 * Fully degrades to "View all" if the IP detector or geo fails.
 */
export default function DoctorsNearYou() {
    const [city, setCity] = useState(() => sessionStorage.getItem(CITY_KEY) || "");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            // 1. detect city if not already cached
            let detected = city;
            if (!detected) {
                try {
                    const res = await fetch("https://ipapi.co/json/", { cache: "no-store" });
                    if (res.ok) {
                        const j = await res.json();
                        if (j.city) {
                            detected = j.city;
                            sessionStorage.setItem(CITY_KEY, detected);
                            if (!cancelled) setCity(detected);
                        }
                    }
                } catch { /* offline / blocked / rate-limited — that's fine */ }
            }
            // 2. fetch doctors near (server gracefully handles empty city)
            try {
                const { data } = await api.get("/doctors/near", {
                    params: { city: detected || undefined, limit: 4 },
                });
                if (!cancelled) setData(data);
            } catch { /* ignore */ }
            finally { if (!cancelled) setLoading(false); }
        })();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (loading) {
        return (
            <section className="bg-sky-50/40 border-y border-sky-100 py-10" data-testid="doctors-near-you-loading">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center gap-3 text-sm text-stone-500">
                    <Loader2 className="w-4 h-4 animate-spin" /> Finding homeopaths near you…
                </div>
            </section>
        );
    }

    if (!data) return null;

    const totalShown = (data.registered?.length || 0) + (data.internet?.length || 0);
    if (totalShown === 0) {
        // Don't render the band if there's no doctor data at all (avoids dead empty section)
        return null;
    }

    return (
        <section className="bg-sky-50/40 border-y border-sky-100 py-12" data-testid="doctors-near-you">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
                    <div>
                        <div className="label-eyebrow text-sky-700 flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {city ? `Near you · ${city}` : "Featured homeopaths"}
                        </div>
                        <h2 className="font-serif text-3xl sm:text-4xl text-slate-900 mt-2">
                            {city ? `Homeopaths in ${city}` : "Find a homeopath"}
                        </h2>
                    </div>
                    <Link to={`/doctors${city ? `?city=${encodeURIComponent(city)}` : ""}`} className="text-sm text-sky-700 hover:text-sky-800 underline inline-flex items-center" data-testid="see-all-doctors">
                        See all doctors <ChevronRight className="w-4 h-4 ml-1" />
                    </Link>
                </div>

                {data.registered?.length > 0 && (
                    <>
                        <h3 className="text-xs uppercase tracking-widest text-stone-500 mb-3">Registered with us</h3>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                            {data.registered.map((d) => (
                                <div key={d.id} className="bg-white border border-sky-100 p-4" data-testid={`near-reg-${d.id}`}>
                                    <div className="flex items-center gap-2">
                                        <BadgeCheck className="w-4 h-4 text-sky-700 shrink-0" />
                                        <div className="font-serif text-base text-slate-900 truncate">{d.name}</div>
                                    </div>
                                    <div className="text-xs text-stone-500 mt-1 line-clamp-1">{d.specialization}</div>
                                    <div className="text-xs text-stone-500 mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" />{d.city}{d.state ? `, ${d.state}` : ""}</div>
                                    <div className="text-[10px] text-stone-400 mt-2">{d.experience_years || 0} yrs · ★{d.rating || 0}</div>
                                </div>
                            ))}
                        </div>
                    </>
                )}

                {data.show_internet && data.internet?.length > 0 && (
                    <>
                        <h3 className="text-xs uppercase tracking-widest text-stone-500 mb-3">Other publicly-listed clinics</h3>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            {data.internet.map((d) => (
                                <div key={d.id} className="bg-stone-50 border border-stone-200 p-4" data-testid={`near-int-${d.id}`}>
                                    <div className="font-serif text-base text-slate-900 truncate">{d.name}</div>
                                    <div className="text-xs text-stone-500 mt-1 line-clamp-1">{d.specialization}</div>
                                    <div className="text-xs text-stone-500 mt-1 flex items-center gap-1">
                                        <MapPin className="w-3 h-3" />
                                        {[d.area, d.city].filter(Boolean).join(", ")}
                                    </div>
                                    {d.source_url && (
                                        <a href={d.source_url} target="_blank" rel="noreferrer" className="text-[10px] text-sky-700 mt-2 inline-flex items-center underline">
                                            Source <ExternalLink className="w-2.5 h-2.5 ml-1" />
                                        </a>
                                    )}
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </section>
    );
}
